import {type PartialOptions, UI} from "../../../../../../stem-core/ui/UIBase";
import {StyleSheet, registerStyle, styleRule} from "../../../../../../stem-core/ui/Style";
import {Messages} from "../../../../../Messages";
import {BUTTON_TYPE} from "../../../../../Constants";
import {NOOP_FUNCTION} from "../../../../../../stem-core/base/Utils";
import {CoveragePreview} from "./CoveragePreview";
import {isSmallScreen} from "../../../../../UtilsLib";
import {iFrameUserDataService} from "../../../../../services/IFrameUserDataService";
import {SubscriptionFlowType} from "../../../../flows/steps/SubscriptionPlanFlowStep.js";
import {Flow} from "../../../../flows/Flow.js";
import {BlinkButton} from "../../../../../../core/ui/Button.jsx";
import type {SubscriptionOffer} from "../../../../../../client/state/SubscriptionOfferStore";


class SubscriptionCoverageCardStyle extends StyleSheet {
    @styleRule
    container = {
        display: "flex",
        flexDirection: "column",
        width: () => isSmallScreen() ? null : 418,
        minWidth: 280,
        background: this.themeProps.MERCHANT_CARD_BACKGROUND,
        padding: "18px 24px 24px",
        border: "1px solid " + this.themeProps.MERCHANT_CARD_BORDER_COLOR,
        borderRadius: this.themeProps.MERCHANT_FRAME_BORDER_RADIUS,
        boxShadow: this.themeProps.MERCHANT_CARD_BOX_SHADOW,
    };

    @styleRule
    buttonsContainer = {
        display: "flex",
        flexDirection: "column",
        alignSelf: "flex-end",
        width: "100%",
        marginTop: "auto",
        ">:not(:first-child)": {
            marginTop: 10,
        }
    }
}

interface SubscriptionCoverageCardOptions {
    coverage?: any;
    offers?: SubscriptionOffer[];
    onSubscriptionOfferSelected?: (...args: any[]) => void;
}

@registerStyle(SubscriptionCoverageCardStyle)
export class SubscriptionCoverageCard extends UI.Element<SubscriptionCoverageCardOptions> {
    getDefaultOptions(): PartialOptions<SubscriptionCoverageCard> {
        return {
            ...super.getDefaultOptions(),
            coverage: null,
            offers: [],
            onSubscriptionOfferSelected: NOOP_FUNCTION,
            testId: "subscriptionCoverageCard",
        };
    }

    getFormattedSubscriptionOfferPrice(offer: SubscriptionOffer) {
        return `${Flow.subscriptionPlan.getSubscriptionOfferPrice(offer)}/${offer.getCycleDuration(true)}`
    }

    getOfferLabel(offer: SubscriptionOffer) {
        const formattedPrice = this.getFormattedSubscriptionOfferPrice(offer);

        if (Flow.subscriptionPlan.subscriptionFlowType === SubscriptionFlowType.GIFT_SUBSCRIPTION) {
            return Messages.giftSubscriptionButtonLabel(formattedPrice);
        }

        if (Flow.subscriptionPlan.subscriptionFlowType === SubscriptionFlowType.SUBSCRIBE) {
            const subscriptionOffers = this.options.offers;

            return subscriptionOffers.length > 1 ? Messages.subscribeInlineFlowButton(formattedPrice) : Messages.subscribe
        }

        return this.userHasEquivalentSubscription(offer) ? Messages.subscribedButtonLabel(formattedPrice) : Messages.changePlanButtonLabel(formattedPrice);
    }

    userHasEquivalentSubscription(offer: SubscriptionOffer) {
        if (Flow.subscriptionPlan.subscriptionFlowType === SubscriptionFlowType.GIFT_SUBSCRIPTION) {
            return false;
        }

        if (Flow.subscriptionPlan.subscriptionFlowType === SubscriptionFlowType.SUBSCRIBE) {
            return false;
        }

        const activeSubscription = iFrameUserDataService.getActiveSubscription();
        return !!activeSubscription && activeSubscription.billingPlan === offer.billingPlan && activeSubscription.coverage === offer.coverage;
    }

    render() {
        const {styleSheet} = this;
        const {coverage, onSubscriptionOfferSelected} = this.options;
        const subscriptionOffers = this.options.offers;

        if (subscriptionOffers.length === 0) {
            return null;
        }

        return [
            <CoveragePreview coverage={coverage} subscriptionOffers={subscriptionOffers}/>,
            <div className={styleSheet.buttonsContainer}>
                {
                    subscriptionOffers.map((offer, index) => <BlinkButton
                        type={index ? BUTTON_TYPE.SECONDARY : BUTTON_TYPE.PRIMARY}
                        disabled={this.userHasEquivalentSubscription(offer)}
                        onClick={() => onSubscriptionOfferSelected(offer)}
                        label={this.getOfferLabel(offer)}
                    />)
                }
            </div>
        ];
    }
}
